import { makeSelectedEvent } from 'app/pages/TicketSalePage/slice/selectors';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Ticket,
  TicketTypeBundle,
  TicketTypeBundleItem,
} from 'types/TicketsState';
import { bundleHasCalendar, getBundleTotalPrice } from 'utils/bundles';
import { formatDateAndTimeOnTimeZone } from 'utils/events';
import { OveitIcon } from '../OveitIcon';
import { TicketSalePageEvent } from 'app/pages/TicketSalePage/slice/types';
import {
  getTicketTypeTotalPriceWithoutDiscount,
  roundUpDecimals,
} from 'utils/tickets';
import { formatMoney } from 'utils/formatMoney';

interface TicketTypeBundleGenericsProps {
  ticketTypeBundle: TicketTypeBundle;
  ticketTypes: Ticket[];
  displaySoldFromDate: boolean;
  displaySoldToDate: boolean;
  sold_from: string | null;
  sold_to: string | null;
  soldOut: boolean;
  selectedDate: DateTime | undefined;
}

const TicketTypeBundleGenerics: React.FC<TicketTypeBundleGenericsProps> = ({
  ticketTypeBundle,
  ticketTypeBundle: { name, price, items },
  ticketTypes,
  displaySoldFromDate,
  displaySoldToDate,
  sold_from,
  sold_to,
  soldOut,
  selectedDate,
}) => {
  const { t, i18n } = useTranslation();

  const selectedEvent = useSelector(makeSelectedEvent);
  const {
    timeInterval: { endsAt },
    dateTimeFormat: { timezone, dateFormat, timeFormat },
  } = selectedEvent;

  return (
    <div className="flex flex-col sm:flex-row">
      <div className="flex flex-col justify-center">
        <h3 className="p1-medium mb-[4px] max-w-[180px] md:max-w-[240px]">
          {name}
        </h3>
        {displaySoldToDate ? (
          <span className="p3-regular text-neutral-800">
            {t('ticketSale.saleEndOn')}{' '}
            {formatDateAndTimeOnTimeZone(
              (sold_to || endsAt) as string,
              dateFormat,
              timeFormat,
              timezone,
              t('common.at'),
            )}
          </span>
        ) : null}
        {displaySoldFromDate ? (
          <span className="p3-regular text-neutral-800">
            {t('ticketSale.saleStartOn')}{' '}
            {formatDateAndTimeOnTimeZone(
              sold_from as string,
              dateFormat,
              timeFormat,
              timezone,
              t('common.at'),
            )}
          </span>
        ) : null}
        {selectedDate && bundleHasCalendar(ticketTypes) ? (
          <span className="p4-regular text-neutral-800">
            ({selectedDate.setLocale(i18n.language).toFormat('cccc, LLLL d')})
          </span>
        ) : null}
        <span className="flex items-center gap-[4px] premium-label w-fit mt-[8px]">
          <OveitIcon name="sparkle" />
          {t('ticketSale.bundleLabel', {
            savedAmount: getSavedAmount(
              selectedEvent,
              ticketTypeBundle,
              ticketTypes,
              items,
            ),
          })}
        </span>
        <div className="flex gap-[8px] mt-[4px]">
          {soldOut ? <p className="red-label">{t('common.soldOut')}</p> : null}
        </div>
      </div>
    </div>
  );
};

export default TicketTypeBundleGenerics;

const getSavedAmount = (
  event: TicketSalePageEvent,
  bundle: TicketTypeBundle,
  ticketTypes: Ticket[],
  items: TicketTypeBundleItem[],
) => {
  const ticketTypesTotalPrice = getTicketTypesTotalPriceWithoutDiscount(
    event,
    ticketTypes,
    items,
  );
  const bundleTotalPrice = getBundleTotalPrice(event, bundle, ticketTypes);

  const discount = roundUpDecimals(ticketTypesTotalPrice - bundleTotalPrice);

  return formatMoney(discount, event.currency);
};

const getTicketTypesTotalPriceWithoutDiscount = (
  event: TicketSalePageEvent,
  ticketTypes: Ticket[],
  items: TicketTypeBundleItem[],
) =>
  ticketTypes.reduce(
    (acc, ticketType) =>
      acc +
      getTicketTypeTotalPriceWithoutDiscount(
        event,
        ticketType,
        items.find(item => item.idTicketType === ticketType.id)!.quantity,
      ),
    0,
  );
